import { APP_VERSION } from '@/version';
import { useState } from 'react';
import { ArrowRight, Monitor, ImageIcon, Upload, LayoutGrid, CheckCircle, Cpu, Calendar, Youtube, Clock, Wifi } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

function OverlaysTab() {
  return (
    <div>
      {/* Slideshows */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold mb-2">Sponsor Slideshows</h2>
        <p className="text-muted-foreground text-lg mb-8">
          A slideshow is a rotating loop of sponsor banners that plays inside your broadcast overlay
          during games. Each sponsor gets their logo on screen for a set number of seconds before the
          next one appears.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader>
              <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center mb-2">
                <Upload className="h-5 w-5 text-blue-500" />
              </div>
              <CardTitle className="text-base">1. Upload Sponsor Logos</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              Your club logs into CSM and uploads sponsor banner images — just PNG files sized to fit
              the overlay. No design software needed.
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="h-10 w-10 rounded-lg bg-green-500/10 flex items-center justify-center mb-2">
                <LayoutGrid className="h-5 w-5 text-green-500" />
              </div>
              <CardTitle className="text-base">2. Build Your Slideshow</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              Arrange the logos in any order and set how long each one displays. Mark one slideshow
              as "active" and it plays during every broadcast.
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="h-10 w-10 rounded-lg bg-purple-500/10 flex items-center justify-center mb-2">
                <Monitor className="h-5 w-5 text-purple-500" />
              </div>
              <CardTitle className="text-base">3. It Appears on Screen</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              The slideshow shows up automatically inside your broadcast overlay. Update sponsors in
              CSM and the change goes live immediately.
            </CardContent>
          </Card>
        </div>

        <Card className="bg-muted/40 border-muted">
          <CardContent className="p-5">
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">Self-service.</strong> Facility staff can log in, upload new sponsors, reorder the
              slideshow, and set durations entirely on their own — no administrator involvement required.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Overlays */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold mb-2">Broadcast Overlays</h2>
        <p className="text-muted-foreground text-lg mb-8">
          An overlay is the layer of graphics that sits on top of your camera feed in OBS —
          the scoreboard, the sponsor banner area, the event ticker. CSM lets you build and manage
          those graphics without any coding.
        </p>

        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">What's in an overlay?</h3>
          <ul className="space-y-3 max-w-lg">
            {[
              { label: 'Slideshow widget', desc: 'The rotating sponsor banner area' },
              { label: 'Scoreboard', desc: 'Team names, ends, and scores' },
              { label: 'Clock / timer', desc: 'Game or shot clock' },
              { label: 'Event ticker', desc: 'Scrolling text for event info' },
              { label: 'Logo / watermark', desc: 'Club or broadcast branding' },
            ].map((item) => (
              <li key={item.label} className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 shrink-0" />
                <span>
                  <strong className="text-foreground">{item.label}</strong>
                  <span className="text-muted-foreground"> — {item.desc}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <Card className="bg-muted/40 border-muted">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <ImageIcon className="h-5 w-5 text-primary mt-0.5 shrink-0" />
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">One overlay, many uses.</strong> The same overlay URL works in OBS, vMix, Wirecast,
                or any browser. You can even open it in a regular browser window to preview exactly what
                your viewers will see before going live.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Who manages what */}
      <div>
        <h2 className="text-2xl font-bold mb-6">Who manages what?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="border-blue-200 dark:border-blue-800">
            <CardHeader>
              <CardTitle className="text-blue-600 dark:text-blue-400 text-base">Your Club Staff</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-muted-foreground">
              <p>✓ Upload and replace sponsor logos</p>
              <p>✓ Set how long each sponsor appears</p>
              <p>✓ Switch between seasonal slideshows</p>
              <p>✓ Preview changes before they go live</p>
            </CardContent>
          </Card>
          <Card className="border-purple-200 dark:border-purple-800">
            <CardHeader>
              <CardTitle className="text-purple-600 dark:text-purple-400 text-base">CSM Administrator</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-muted-foreground">
              <p>✓ Build and design overlay layouts</p>
              <p>✓ Set up new facilities and sheets</p>
              <p>✓ Configure OBS browser source URLs</p>
              <p>✓ Manage access for facility accounts</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function StreamingTab() {
  return (
    <div>
      {/* How It Works */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold mb-2">How It Works</h2>
        <p className="text-muted-foreground text-lg mb-8">
          There are two pieces: the <strong className="text-foreground">CSM server</strong> (in the cloud)
          and the <strong className="text-foreground">CSM agent</strong> (a small program that runs on each
          facility's broadcast computer).
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          <Card className="border-blue-200 dark:border-blue-800">
            <CardHeader>
              <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center mb-2">
                <Monitor className="h-5 w-5 text-blue-500" />
              </div>
              <CardTitle className="text-base">The CSM Server</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-2">
              <p>The central brain. Administrators use it to set up which games are being streamed,
              on which sheets, and at what times.</p>
              <p>It stores the schedule, holds the YouTube credentials securely, and waits for
              the on-site agent to check in.</p>
            </CardContent>
          </Card>

          <Card className="border-green-200 dark:border-green-800">
            <CardHeader>
              <div className="h-10 w-10 rounded-lg bg-green-500/10 flex items-center justify-center mb-2">
                <Cpu className="h-5 w-5 text-green-500" />
              </div>
              <CardTitle className="text-base">The On-Site Agent</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-2">
              <p>A lightweight program that runs quietly in the background on the facility's
              broadcast computer — the same machine that runs OBS.</p>
              <p>It checks in with the server every minute, receives the day's schedule, and
              handles everything from there automatically.</p>
            </CardContent>
          </Card>
        </div>

        <h3 className="text-lg font-semibold mb-5">What happens on game day</h3>
        <div className="space-y-4 mb-10">
          {[
            { icon: Calendar, color: 'text-blue-500', bg: 'bg-blue-500/10', title: 'Club staff enters the schedule', desc: 'Before the event, club staff log into CSM and enter game times, sheet assignments, and team names. Schedules can be entered manually, imported from a CSV, or synced automatically from Google Sheets or CurlingClubManager.' },
            { icon: Wifi, color: 'text-green-500', bg: 'bg-green-500/10', title: 'Agent checks in automatically', desc: 'The agent polls the server every 60 seconds. When the schedule is ready, the agent downloads it silently — no action needed at the facility.' },
            { icon: Clock, color: 'text-orange-500', bg: 'bg-orange-500/10', title: 'Stream starts on time', desc: 'At the scheduled start time, the agent sends a command to OBS to begin streaming. The broadcast goes live automatically — even if no one is in the room.' },
            { icon: Youtube, color: 'text-red-500', bg: 'bg-red-500/10', title: 'YouTube title is updated', desc: 'About 30 seconds after the stream goes live, CSM renames the YouTube broadcast to include the team names, sheet, and event.' },
            { icon: Monitor, color: 'text-purple-500', bg: 'bg-purple-500/10', title: 'Stream stops when the game ends', desc: 'At the scheduled end time, the agent tells OBS to stop streaming. The broadcast ends automatically and the YouTube recording is saved.' },
          ].map((item) => (
            <div key={item.title} className="flex gap-4 items-start">
              <div className={`h-9 w-9 rounded-lg ${item.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                <item.icon className={`h-4 w-4 ${item.color}`} />
              </div>
              <div>
                <p className="font-semibold text-foreground text-sm">{item.title}</p>
                <p className="text-muted-foreground text-sm mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <Card className="bg-muted/40 border-muted">
          <CardContent className="p-5">
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">Hands-free from start to finish.</strong> Once the schedule is entered, the facility
              doesn't need to do anything. The agent handles OBS start, OBS stop, and YouTube title
              — even if the internet connection is briefly interrupted during the event.
            </p>
          </CardContent>
        </Card>

        <Card className="bg-muted/40 border-muted mt-4">
          <CardContent className="p-5">
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">No schedule? No problem.</strong> The agent includes a built-in dashboard for starting
              an unscheduled stream on the fly. Enter the event name and team names at broadcast time
              and the agent handles the rest — including setting the YouTube title automatically using
              the same format as scheduled streams.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Who manages what */}
      <div>
        <h2 className="text-2xl font-bold mb-6">Who manages what?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="border-blue-200 dark:border-blue-800">
            <CardHeader>
              <CardTitle className="text-blue-600 dark:text-blue-400 text-base">Your Club Staff</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-muted-foreground">
              <p>✓ Enter and manage your facility's game schedule</p>
              <p>✓ Import schedules via CSV or sync from Google Sheets / CurlingClubManager</p>
              <p>✓ Pause, cancel, or reschedule individual streams</p>
              <p>✓ Set blackout dates when your facility won't be streaming</p>
              <p>✓ Start ad hoc streams through the agent dashboard (no schedule needed)</p>
              <p>✓ View live agent status and stream logs</p>
            </CardContent>
          </Card>
          <Card className="border-purple-200 dark:border-purple-800">
            <CardHeader>
              <CardTitle className="text-purple-600 dark:text-purple-400 text-base">CSM Administrator</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-muted-foreground">
              <p>✓ Install and configure the on-site agent (once per facility)</p>
              <p>✓ Connect YouTube channel (once per facility)</p>
              <p>✓ Set global blackout dates across all facilities</p>
              <p>✓ Monitor agent and stream status across all facilities</p>
              <p>✓ Manage schedules for any facility if needed</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default function HomePage() {
  const [activeTab, setActiveTab] = useState<'overlays' | 'streaming'>('overlays');

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <img
                  src="/curling-stadium-logo.png"
                  alt="Curling Stadium Manager"
                  className="h-8 w-8 object-contain"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
                <span className="font-bold text-xl">Curling Stadium Manager</span>
              </div>
              <Badge variant="secondary">v{APP_VERSION}</Badge>
            </div>
            <nav className="flex items-center space-x-4">
              <Button variant="ghost" asChild>
                <Link to="/login">Sign In</Link>
              </Button>
              <Button asChild>
                <Link to="/login">Get Started</Link>
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="text-center max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-6xl font-bold tracking-tight text-gradient mb-6">
            Curling Stadium Manager
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Broadcast overlay management, automated stream scheduling, and YouTube integration
            for multi-sheet curling facilities.
          </p>
          <Button size="lg" asChild>
            <Link to="/login">
              Sign In
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Tabbed About Section */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {/* Tab bar */}
        <div className="flex border-b mb-8">
          <button
            onClick={() => setActiveTab('overlays')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'overlays'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Overlays &amp; Slideshows
          </button>
          <button
            onClick={() => setActiveTab('streaming')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'streaming'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Automated Streaming
          </button>
        </div>

        {activeTab === 'overlays' ? <OverlaysTab /> : <StreamingTab />}
      </section>

      {/* Footer */}
      <footer className="border-t bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-muted-foreground space-y-2">
            <p>&copy; 2026 Curling Stadium Manager. Built for the curling broadcast community.</p>
            <p className="text-sm">
              <a href="/privacy.html" className="hover:text-foreground underline">Privacy Policy</a>
              <span className="mx-2">·</span>
              <a href="/terms.html" className="hover:text-foreground underline">Terms of Service</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
